import { useState, useRef, useEffect } from 'react'

interface DataTableEditableCellProps {
  value: string | null
  rowId: string | number
  columnId: string
  isEditing: boolean
  onStartEdit: () => void
  onSave: (value: string) => void
  onCancel: () => void
  onTab?: (value: string) => void
  onShiftTab?: (value: string) => void
}

export function DataTableEditableCell({
  value,
  isEditing,
  onStartEdit,
  onSave,
  onCancel,
  onTab,
  onShiftTab
}: DataTableEditableCellProps) {
  const [editValue, setEditValue] = useState(value || '')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  useEffect(() => {
    setEditValue(value || '')
  }, [value])

  const handleSave = () => {
    onSave(editValue)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave()
    } else if (e.key === 'Escape') {
      setEditValue(value || '')
      onCancel()
    } else if (e.key === 'Tab') {
      if (e.shiftKey && onShiftTab) {
        e.preventDefault()
        onShiftTab(editValue)
      } else if (!e.shiftKey && onTab) {
        e.preventDefault()
        onTab(editValue)
      }
    }
  }

  if (isEditing) {
    return (
      <div className="absolute inset-0 flex items-center px-4">
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="w-full h-9 px-2.5 text-sm font-normal bg-input-bg border border-control-border rounded-lg shadow-sm transition-[outline] focus:outline focus:outline-2 focus:outline-focus-ring focus:outline-offset-1"
        />
      </div>
    )
  }

  return (
    <div
      className="absolute inset-0 flex items-center px-2 cursor-pointer group/cell"
      onClick={onStartEdit}
    >
      <div className="group-hover/cell:bg-accent px-2 rounded-[10px] text-content-body text-sm h-9 flex items-center w-full min-w-0">
        <span className="whitespace-nowrap overflow-hidden text-ellipsis block">{value}</span>
      </div>
    </div>
  )
}
